// src/utils/passwordReset.js
// Forgot password on the client (FORGOT_PASSWORD_DESIGN.md 4.2; strings
// section 5). Display and flow only: the server answers every request the
// same way (sendPasswordReset always resolves {ok: true} once its limits
// pass), and Firebase checks and applies the one-time code on the reset page
// (verifyPasswordResetCode, confirmPasswordReset).
//
// This module holds every new string word for word, the signup password
// rules (SignUpWizard.js, word for word), how a server refusal or a Firebase
// error maps to what a screen shows, the Forgot page's send with its
// no-App-Check fallback (sendResetRequest), the reset page's steps after the new
// password is saved (finishReset, run with the real Firebase calls by the
// screen and with stubs by the harness), what the done screen says about the
// ways into the account a new password leaves in place (review FP-ADV-1),
// and a one-shot hand-off of the
// address typed on the Login screen (in memory: react-navigation on the web
// writes route params into the address bar, so an address never travels as
// a param).
//
// Copy rules: no em dashes, never "AI", plain words, CritCast. No screen
// shows a full address (streamers capture their screens live): the masked
// one comes from maskEmail in membershipJoin.js. ASCII only.
//
// Zero imports: unit-tested in plain node
// (web-landing/scripts/test-password-reset.mjs).

// ---------------------------------------------------------------------------
// Strings (section 5)
// ---------------------------------------------------------------------------

// LS1: the Login screen link under the password field.
export const FORGOT_LINK_LABEL = 'Forgot password?';
// SU1: the signup email error when the address already has an account
// (was "This email is already registered. Try logging in.").
export const SIGNUP_EMAIL_TAKEN_LINE = 'This email is already registered. Sign in, or reset your password from the sign-in page.';

// The Forgot page (FP1 to FP12).
export const FORGOT_TITLE = 'Reset your password';
export const FORGOT_LEAD = "Enter the email address you use for CritCast. We'll email you how to get back in.";
export const EMAIL_LABEL = 'Email';
export const SEND_LINK_LABEL = 'Send link';
export const FORGOT_SENT_LINE = 'If that address has a CritCast account, we sent it an email. It can take a few minutes. Check your spam folder too.';
export const SEND_AGAIN_LABEL = 'Send it again';
export const BACK_TO_SIGN_IN_LABEL = 'Back to sign in';
export const INVALID_EMAIL_LINE = 'Enter a valid email address.';
export const FORGOT_FAILED_LINE = 'Something went wrong. Please try again.';

// The reset page (RP1 to RP23).
export const RESET_TITLE = 'Choose a new password';
export const CHECKING_LINE = 'Checking your link...';
export const forAccountLine = (masked) => `For the CritCast account at ${masked}.`;
// RP20: under RP3 when this browser is signed in to a different account
// (reviews UX-3, FP-ADV-6: saving then does not sign in to the reset account).
export const OTHER_ACCOUNT_LINE = "This browser is signed in to a different account. Saving the new password won't switch to this one.";
export const NEW_PASSWORD_LABEL = 'New password';
export const CONFIRM_PASSWORD_LABEL = 'Confirm new password';
export const PASSWORD_HINT = 'At least 8 characters.';
export const SAVE_PASSWORD_LABEL = 'Save new password';
export const SAVE_FAILED_LINE = "We couldn't save your new password. Please try again.";
export const savedAllLine = (masked) => `Your new password is saved. You're signed in as ${masked}. Other browsers and devices will be signed out within an hour.`;
export const savedSignedInLine = (masked) => `Your new password is saved. You're signed in as ${masked}.`;
export const SAVED_SIGN_IN_LINE = 'Your new password is saved. Sign in with it to continue.';
// RP19: right after the save, while finishReset runs (review UX-1).
export const FINISHING_LINE = 'Your new password is saved. Signing you in...';
// RP22: the account has a stream key (a new password does not change it).
// "in an hour": a session from before the reset keeps a working ID token for
// up to an hour (Firestore and every callable accept it), so a key made
// sooner can still be read by it (review FP-R3-2).
export const STREAM_KEY_LINE = 'Your stream key is the same. If someone else may know it, use Regenerate in the Streaming Hub in an hour, once other sessions have ended.';
// RP23: what is left in place could not be read (or the page is not signed
// in to the account), so the page names the kinds instead. Google is named
// while check P3 (does production drop Google on a reset?) is open (review
// FP-R2-5).
export const OTHER_WAYS_UNKNOWN_LINE = "A new password doesn't remove a phone number, a Google sign-in, Discord or Twitch linked to this account, or change your stream key. If you think someone else used this account, contact support@critcash.com.";
export const BACK_TO_CRITCAST_LABEL = 'Back to CritCast';
export const SIGN_IN_LABEL = 'Sign in';
// RP14 (review UX-1: a reset that already saved can end here after a reload).
export const RESET_LINK_USED_LINE = 'This link has expired or was already used. If you already chose a new password, sign in with it.';
export const GET_NEW_LINK_LABEL = 'Get a new link';
export const CHECK_FAILED_LINE = "We couldn't open this link right now. Try again in a moment.";
export const TRY_AGAIN_LABEL = 'Try again';
export const NO_CODE_LINE = 'Open the link in your email again, or ask for a new one.';

// ---------------------------------------------------------------------------
// The signup password rules (SignUpWizard.js validateStep and its
// auth/weak-password line, word for word)
// ---------------------------------------------------------------------------

export const MIN_PASSWORD_LENGTH = 8;
export const PASSWORD_REQUIRED_LINE = 'Password is required';
export const PASSWORD_SHORT_LINE = 'Password must be at least 8 characters';
export const CONFIRM_REQUIRED_LINE = 'Please confirm your password';
export const PASSWORD_MISMATCH_LINE = 'Passwords do not match';
export const PASSWORD_WEAK_LINE = 'Password is too weak. Use at least 8 characters.';

/** {} when the pair passes; else {password?, confirm?} with signup's lines. */
export function newPasswordErrors(password, confirm) {
  const errors = {};
  if (!password) errors.password = PASSWORD_REQUIRED_LINE;
  else if (password.length < MIN_PASSWORD_LENGTH) errors.password = PASSWORD_SHORT_LINE;
  if (!confirm) errors.confirm = CONFIRM_REQUIRED_LINE;
  else if (password !== confirm) errors.confirm = PASSWORD_MISMATCH_LINE;
  return errors;
}

// ---------------------------------------------------------------------------
// The address (the server's parseEmail word for word)
// ---------------------------------------------------------------------------

export const MAX_EMAIL_LENGTH = 254;

/**
 * The typed address, trimmed, or null: at most 254 characters, no spaces,
 * exactly one '@' with text before it, and a dot inside the part after it
 * with text on both sides.
 */
export function parseEmail(v) {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!s || s.length > MAX_EMAIL_LENGTH || /\s/.test(s)) return null;
  const parts = s.split('@');
  if (parts.length !== 2 || !parts[0]) return null;
  return /^[^.]+(\.[^.]+)*\.[^.]+$/.test(parts[1]) ? s : null;
}

// The Login screen's typed address, handed to the Forgot page once: the page
// reads it while it mounts (peek is idempotent, so a StrictMode double render
// reads the same value) and clears it in an effect.
let prefill = null;
export function setResetPrefill(email) {
  prefill = typeof email === 'string' ? email.trim().slice(0, MAX_EMAIL_LENGTH) : null;
}
export function peekResetPrefill() {
  return prefill || '';
}
export function clearResetPrefill() {
  prefill = null;
}

// ---------------------------------------------------------------------------
// What the Forgot page shows for a refusal
// ---------------------------------------------------------------------------

// The server's tokens (functions/modules/passwordReset.js). The sentence
// after the token is shown as written (SV2 and SV3 carry the cooldown).
const SENTENCE_TOKENS = new Set(['reset-limit', 'reset-busy', 'reset-unavailable']);

/**
 * sendPasswordReset's refusal -> {field, text}: field true = under the email
 * field (FP8). Anything without a known token (the network, an internal
 * error) is FP12.
 */
export function forgotErrorLine(err) {
  const raw = String((err && err.message) || '').trim();
  const m = /^([a-z][a-z-]*):\s*(.+)$/.exec(raw);
  if (m && m[1] === 'reset-email-invalid') return { field: true, text: INVALID_EMAIL_LINE };
  if (m && SENTENCE_TOKENS.has(m[1])) return { field: false, text: m[2].trim() };
  return { field: false, text: FORGOT_FAILED_LINE };
}

// ---------------------------------------------------------------------------
// Sending the Forgot page's request (review FP-R2-1)
// ---------------------------------------------------------------------------

// How long the Forgot page waits for the call made with App Check before it
// checks whether the reCAPTCHA library has loaded (until it loads, the App
// Check step waits, with no deadline of its own).
export const APP_CHECK_WAIT_MS = 15000;

/** true once the reCAPTCHA library has loaded (its execute exists; the api.js loader alone defines only ready). */
export function recaptchaLibraryLoaded() {
  const g = typeof globalThis !== 'undefined' ? globalThis.grecaptcha : undefined;
  return !!g && typeof g.execute === 'function';
}

/** true for an App Check SDK refusal (code 'appCheck/...'): the request never left the browser. */
export function isAppCheckFailure(err) {
  return !!err && typeof err.code === 'string' && err.code.startsWith('appCheck/');
}

/**
 * sendPasswordReset from the Forgot page: first with a limited-use App
 * Check token; once more WITHOUT App Check when App Check cannot attest (an
 * appCheck/... refusal: the SDK sent nothing, so nothing is counted twice)
 * or when the first call has not answered within timeoutMs AND the
 * reCAPTCHA library has not loaded yet (the App Check step is still waiting
 * for it, so nothing has been sent yet). With the library loaded, the page
 * keeps waiting for the first call (a cold server), so a call that is only
 * slow is not sent twice (review R3-01). The SDK's 70 s timeout starts only
 * after the App Check token, so a token step that never settles keeps the
 * page waiting with no deadline (review FP-R4-3). And this cannot tell "not
 * loaded yet" from "never loads": a library that loads after the check lets
 * the first call get its token and go out too, so the server sees two
 * requests (a second set of counter slots, possibly a second email; review
 * R4-02). The server takes a call without a usable token on its
 * no-App-Check counters (design 4.1). Any other refusal is thrown as it came.
 * deps: {withAppCheck(), withoutAppCheck()}; timeoutMs, setTimer,
 * clearTimer and recaptchaLoaded are for the harness.
 */
export async function sendResetRequest({ withAppCheck, withoutAppCheck, timeoutMs = APP_CHECK_WAIT_MS, setTimer = setTimeout, clearTimer = clearTimeout, recaptchaLoaded = recaptchaLibraryLoaded }) {
  const TIMED_OUT = {};
  let timer = null;
  const late = new Promise((resolve) => { timer = setTimer(() => resolve(TIMED_OUT), timeoutMs); });
  let first;
  try {
    const pending = withAppCheck();
    first = await Promise.race([pending, late]);
    if (first === TIMED_OUT && recaptchaLoaded()) first = await pending;
  } catch (err) {
    if (!isAppCheckFailure(err)) throw err;
    return withoutAppCheck();
  } finally {
    clearTimer(timer);
  }
  return first === TIMED_OUT ? withoutAppCheck() : first;
}

// ---------------------------------------------------------------------------
// The reset page
// ---------------------------------------------------------------------------

/** The code from the route params, read once: a string with no spaces, at most 512 characters; else ''. */
export function readResetCode(params) {
  const c = params && typeof params.code === 'string' ? params.code.trim() : '';
  return c && c.length <= 512 && !/\s/.test(c) ? c : '';
}

const USED_CODES = new Set(['auth/expired-action-code', 'auth/invalid-action-code', 'auth/user-not-found']);

/** verifyPasswordResetCode's refusal -> 'used' (RP14) | 'disabled' (RestoreAccount) | 'retry' (RP16). */
export function verifyErrorKind(err) {
  const code = err && err.code;
  if (USED_CODES.has(code)) return 'used';
  if (code === 'auth/user-disabled') return 'disabled';
  return 'retry';
}

/**
 * confirmPasswordReset's refusal -> 'weak' (PASSWORD_WEAK_LINE under the
 * field) | 'used' (RP14) | 'disabled' (RestoreAccount) | 'retry' (RP8, stay
 * on the form).
 */
export function confirmErrorKind(err) {
  const code = err && err.code;
  if (code === 'auth/weak-password' || code === 'auth/password-does-not-meet-requirements') return 'weak';
  if (USED_CODES.has(code)) return 'used';
  if (code === 'auth/user-disabled') return 'disabled';
  return 'retry';
}

/**
 * true when this browser is signed in to an account other than the one the
 * code belongs to (no email on it counts as other). currentUser =
 * auth.currentUser (or null).
 */
export function otherAccountSignedIn(currentUser, email) {
  if (!currentUser) return false;
  const mine = typeof currentUser.email === 'string' ? currentUser.email.trim().toLowerCase() : '';
  const theirs = typeof email === 'string' ? email.trim().toLowerCase() : '';
  return !mine || mine !== theirs;
}

const isStrOrNull = (v) => v === null || typeof v === 'string';

/** endOtherSessions' `others` when it has the expected shape, else null. */
export function readOthers(res) {
  const o = res && res.data && res.data.others;
  if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
  if (!['phone', 'google', 'discord', 'twitch'].every((k) => isStrOrNull(o[k])) || typeof o.streamKey !== 'boolean') return null;
  return { phone: o.phone, google: o.google, discord: o.discord, twitch: o.twitch, streamKey: o.streamKey };
}

// How long finishReset waits after the save before it signs in: the reset
// moves the account's revoke time in whole seconds, and endOtherSessions
// refuses a sign-in from that same second (it may be someone signing in
// with the old password just before the reset), so the page's own sign-in
// must land in a later second (review FP-R3-1). That refusal covers only
// endOtherSessions: Firebase itself still accepts a sign-in from the reset's
// own second as a full session (it can change the password) until the
// owner's revoke in step 2, and outcomes that skip step 2 revoke nothing
// (review FP-R4-1; design 6.3).
export const RESET_SETTLE_MS = 1050;

/**
 * The steps after confirmPasswordReset succeeded (the password IS changed,
 * so every outcome says so):
 *   0. this browser is signed in to a different account: stop, no sign-in
 *      (review FP-ADV-6: a link to someone else's account must not move
 *      this browser into it) -> 'other_account' (RP11);
 *   1. wait RESET_SETTLE_MS, then sign in with the new password;
 *   2. endOtherSessions() (revokes every refresh token, this one included;
 *      answers what a new password leaves in place);
 *   3. sign in again.
 * -> {outcome, others}: outcome 'all' (RP9) | 'signed_in' (RP10: step 2
 *    failed, no sign-out claim) | 'signed_out' (RP11: a sign-in failed) |
 *    'other_account' (RP11); others = readOthers(step 2's answer) whenever
 *    step 2 answered ('all', or 'signed_out' after step 3 failed: step 1
 *    proved the new password, so the list may show; review FP-R3-5), else
 *    null. A failed sign-in (step 1 or 3) also signs this browser out:
 *    a session still here is one the reset or step 2 revoked, and it would
 *    end within the hour anyway (review R2-04: step 1 can fail while the
 *    same account is signed in here, by Google for example).
 * deps: {signIn(email, password), endOtherSessions(), signOut()}; sleep(ms)
 * is for the harness.
 */
export async function finishReset({ email, password, signedInElsewhere, signIn, endOtherSessions, signOut, sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)) }) {
  if (signedInElsewhere) return { outcome: 'other_account', others: null };
  await sleep(RESET_SETTLE_MS);
  try {
    await signIn(email, password);
  } catch (_) {
    try { if (signOut) await signOut(); } catch (__) { /* already out */ }
    return { outcome: 'signed_out', others: null };
  }
  let res;
  try {
    res = await endOtherSessions();
  } catch (_) {
    return { outcome: 'signed_in', others: null };
  }
  try {
    await signIn(email, password);
  } catch (_) {
    try { if (signOut) await signOut(); } catch (__) { /* already out */ }
    return { outcome: 'signed_out', others: readOthers(res) };
  }
  return { outcome: 'all', others: readOthers(res) };
}

/** The done line for finishReset's outcome (RP9 / RP10 / RP11). */
export function doneLine(outcome, masked) {
  if (outcome === 'all') return savedAllLine(masked);
  if (outcome === 'signed_in') return savedSignedInLine(masked);
  return SAVED_SIGN_IN_LINE;
}

/** "A", "A and B", "A, B and C". */
function joinAnd(items) {
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * RP21: the sign-in ways a new password leaves on the account, or '' when
 * there are none. others = readOthers(...).
 */
export function otherWaysLine(others) {
  if (!others) return '';
  const items = [];
  if (others.phone !== null && others.phone !== undefined) items.push(others.phone ? `a phone number ending in ${others.phone}` : 'a phone number');
  if (others.google !== null && others.google !== undefined) items.push(others.google ? `Google (${others.google})` : 'Google');
  if (others.discord !== null && others.discord !== undefined) items.push(others.discord ? `Discord (${others.discord})` : 'Discord');
  if (others.twitch !== null && others.twitch !== undefined) items.push(others.twitch ? `Twitch (${others.twitch})` : 'Twitch');
  if (!items.length) return '';
  return items.length === 1
    ? `Still linked to this account: ${items[0]}. A new password doesn't remove it. If it isn't yours, contact support@critcash.com.`
    : `Still linked to this account: ${joinAnd(items)}. A new password doesn't remove them. If one isn't yours, contact support@critcash.com.`;
}

/**
 * The lines under the done line: with a readable answer (finishReset gives
 * one only after step 2 answered: 'all', or 'signed_out' when the second
 * sign-in failed; review FP-R3-5), RP21 and RP22 as they apply (none when
 * nothing is left); otherwise RP23, so the page never suggests the account
 * is clean (review FP-ADV-1).
 */
export function doneExtraLines(outcome, others) {
  if (others) {
    const lines = [];
    const ways = otherWaysLine(others);
    if (ways) lines.push(ways);
    if (others.streamKey) lines.push(STREAM_KEY_LINE);
    return lines;
  }
  return [OTHER_WAYS_UNKNOWN_LINE];
}
